/**
 * Liquidity Discount Panel Component
 * Implements PRD FR-36
 */

import { useSimulationStore } from '../state/simulationStore';

export function LiquidityDiscountPanel() {
  const { simulation } = useSimulationStore();

  if (!simulation) {
    return null;
  }

  const { equilibrium, params } = simulation;
  const {
    liquidityDiscount,
    riskComponent,
    feeComponent,
    immediacy,
    q1_LT1,
  } = equilibrium;
  const { i, n, eta } = params;

  const hasFees = eta > 0;
  const totalTrade = (n * i) / (n + 1);
  const riskPct = hasFees ? (riskComponent / liquidityDiscount) * 100 : 100;
  const feePct = hasFees ? (feeComponent / liquidityDiscount) * 100 : 0;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        Liquidity Discount Analysis
      </h3>

      {/* Total Discount */}
      <div className="mb-6">
        <div className="flex justify-between items-baseline mb-2">
          <span className="text-sm text-gray-600">Total Liquidity Discount:</span>
          <span className="text-2xl font-bold text-amber-600">
            {liquidityDiscount.toFixed(4)}
          </span>
        </div>
        <div className="text-xs text-gray-500">
          = μ - S₁ = {params.mu.toFixed(2)} - {equilibrium.S1.toFixed(4)}
        </div>
      </div>

      {/* Decomposition Bar */}
      <div className="mb-6">
        <div className="text-sm font-medium text-gray-700 mb-2">Decomposition:</div>
        <div className="h-6 rounded-full overflow-hidden bg-gray-200 flex">
          <div
            className="bg-red-400 flex items-center justify-center text-white text-xs font-medium"
            style={{ width: `${riskPct}%` }}
          >
            {riskPct > 15 && 'Risk'}
          </div>
          {hasFees && (
            <div
              className="bg-blue-400 flex items-center justify-center text-white text-xs font-medium"
              style={{ width: `${feePct}%` }}
            >
              {feePct > 15 && 'Fee'}
            </div>
          )}
        </div>
        <div className="flex justify-between text-xs mt-1 text-gray-600">
          <span>
            Risk: {riskComponent.toFixed(4)} ({riskPct.toFixed(1)}%)
          </span>
          {hasFees && (
            <span>
              Fee: {feeComponent.toFixed(4)} ({feePct.toFixed(1)}%)
            </span>
          )}
        </div>
      </div>

      {/* Component Formulas */}
      <div className="space-y-3 text-sm">
        <div className="p-3 bg-red-50 rounded-lg">
          <div className="font-medium text-red-800">Risk Component</div>
          <div className="font-mono text-red-600 text-xs mt-1">
            (a × σ² × i) / (n + 1) = ({params.a} × {Math.pow(params.sigma, 2)} × {i}) / {n + 1}
          </div>
          <div className="text-red-700 font-bold mt-1">= {riskComponent.toFixed(4)}</div>
        </div>

        {hasFees && (
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="font-medium text-blue-800">Fee Component</div>
            <div className="font-mono text-blue-600 text-xs mt-1">
              (2n / (n + 1)) × η = (2 × {n} / {n + 1}) × {eta}
            </div>
            <div className="text-blue-700 font-bold mt-1">= {feeComponent.toFixed(4)}</div>
          </div>
        )}
      </div>

      {/* Immediacy and Partial Liquidation */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-4">
          {/* Immediacy */}
          <div className="p-3 bg-green-50 rounded-lg">
            <div className="text-sm font-medium text-green-800">Immediacy</div>
            <div className="text-2xl font-bold text-green-600">
              {(immediacy * 100).toFixed(1)}%
            </div>
            <div className="text-xs text-green-600 mt-1">
              n/(n+1) = {n}/{n + 1}
            </div>
          </div>

          {/* Partial Liquidation */}
          <div className="p-3 bg-purple-50 rounded-lg">
            <div className="text-sm font-medium text-purple-800">LT1's Trade</div>
            <div className="text-sm text-purple-600 mt-1">
              <div>Sold: <span className="font-bold">{totalTrade.toFixed(2)}</span></div>
              <div>Retained: <span className="font-bold">{q1_LT1.toFixed(2)}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
